import { useCallback, useEffect, useRef, useState } from 'react';
import { Preloader } from 'konsta/react';
import { CheckCircle2, Clock, Gift, ImagePlus, Minus, Plus, RotateCcw, ShoppingBag, Ticket, Wallet as WalletIcon, Wrench } from 'lucide-react';
import type { I18nKey } from '@harmony/shared';
import { api, actionId, type CreditPackage, type LedgerType, type Payment, type WalletView } from '../api';
import { BottomSheet, EmptyState, PrimaryButton, TopBar, etb, timeLabel } from '../components/ui';
import { inputClass } from '../components/form';
import { haptic } from '../telegram';
import { useErrorText, useLang, useT } from '../i18n';

const LEDGER_ICON: Record<LedgerType, JSX.Element> = {
  CREDIT_PURCHASE: <ShoppingBag size={18} />,
  CREDIT_SPEND: <Minus size={18} />,
  CREDIT_RECHARGE: <RotateCcw size={18} />,
  REFERRAL_REWARD: <Gift size={18} />,
  ADMIN_ADJUSTMENT: <Wrench size={18} />,
  REFUND: <Plus size={18} />,
};

const PAY_STYLE: Record<string, string> = {
  MANUAL_REVIEW: 'bg-amber-500/15 text-amber-700',
  PENDING: 'bg-amber-500/15 text-amber-700',
  VERIFIED: 'bg-green-500/15 text-green-700',
  DENIED: 'bg-red-500/10 text-red-600',
};

/** Client wallet: requests left, buying more, payments waiting for approval, and history. */
export function WalletPage({ openBuy }: { openBuy?: boolean }) {
  const t = useT();
  const lang = useLang();
  const [w, setW] = useState<WalletView | null>(null);
  const [failed, setFailed] = useState(false);
  const [buying, setBuying] = useState(false);

  const load = useCallback(() => {
    api
      .wallet()
      .then((r) => {
        setW(r);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (openBuy && w?.canBuy && w.packages.length) setBuying(true);
  }, [openBuy, !!w]);

  if (!w)
    return (
      <div className="h-full">
        <TopBar title={t('wallet.title')} />
        {failed ? <EmptyState icon="📡" text={t('error.generic')} /> : <div className="flex justify-center pt-24"><Preloader /></div>}
      </div>
    );

  const refill = new Date(w.quota.nextRechargeAt).toLocaleString(lang === 'am' ? 'am-ET' : 'en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  const recentPayments = w.payments.slice(0, 5);

  return (
    <div className="flex h-full flex-col">
      <TopBar title={t('wallet.title')} />
      <div className="flex-1 overflow-y-auto pb-10">
        <div className="mx-4 mt-4 rounded-3xl bg-gradient-to-br from-[#1E1B3A] to-[#3B33A0] p-5 text-white shadow-lg">
          <div className="flex items-center gap-2 text-sm opacity-90">
            <WalletIcon size={16} /> {t('wallet.requests_left')}
          </div>
          <div className="mt-1 text-5xl font-bold">{w.quota.total}</div>
          <div className="mt-3 flex gap-2 text-sm">
            <span className="rounded-full bg-white/20 px-3 py-1">
              {t('wallet.free')}: <b>{w.quota.free}</b>
            </span>
            <span className="rounded-full bg-white/20 px-3 py-1">
              {t('wallet.paid')}: <b>{w.quota.paid}</b>
            </span>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-sm opacity-90">
            <Clock size={14} /> {t('wallet.refill', { time: refill })}
          </div>
        </div>

        <div className="px-4 pt-4">
          {w.canBuy && w.packages.length ? (
            <PrimaryButton onClick={() => setBuying(true)}>
              <ShoppingBag size={18} /> {t('wallet.buy')}
            </PrimaryButton>
          ) : (
            <p className="rounded-2xl bg-tg-secondary p-3 text-center text-sm text-tg-hint">{w.canBuy ? t('pay.no_packages') : t('pay.disabled')}</p>
          )}
        </div>

        {!!recentPayments.length && (
          <section className="mt-6">
            <h2 className="px-4 pb-2 text-sm font-semibold text-tg-hint">{t('wallet.payments')}</h2>
            <div className="mx-4 divide-y divide-black/5 overflow-hidden rounded-2xl bg-tg-secondary">
              {recentPayments.map((p) => (
                <PaymentRow key={p.id} p={p} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-6">
          <h2 className="px-4 pb-2 text-sm font-semibold text-tg-hint">{t('wallet.history')}</h2>
          {!w.history.length ? (
            <p className="px-4 text-sm text-tg-hint">{t('wallet.history_empty')}</p>
          ) : (
            <div className="mx-4 divide-y divide-black/5 overflow-hidden rounded-2xl bg-tg-secondary">
              {w.history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 px-3 py-3">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${h.credits > 0 ? 'bg-green-500/15 text-green-700' : 'bg-tg-bg text-tg-hint'}`}>
                    {LEDGER_ICON[h.type]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{t(`ledger.${h.type}` as I18nKey)}</div>
                    <div className="text-xs text-tg-hint">{timeLabel(h.createdAt, lang)}</div>
                  </div>
                  <span className={`font-semibold ${h.credits > 0 ? 'text-green-700' : ''}`}>
                    {h.credits > 0 ? '+' : ''}
                    {h.credits}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <BuySheet
        open={buying}
        wallet={w}
        onClose={() => setBuying(false)}
        onSent={() => {
          load();
        }}
      />
    </div>
  );
}

function PaymentRow({ p }: { p: Payment }) {
  const t = useT();
  const lang = useLang();
  const name = p.packageName ? (lang === 'am' ? p.packageName.am : p.packageName.en) : t('agent.subscription_title');
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium">
          {name}
          {p.credits ? <span className="text-tg-hint"> · {t('pay.package_line', { credits: p.credits })}</span> : null}
        </span>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${PAY_STYLE[p.status] ?? 'bg-tg-bg text-tg-hint'}`}>
          {t(`pay.status.${p.status}` as I18nKey)}
        </span>
      </div>
      <div className="text-sm text-tg-hint">
        {etb(p.amount)} ETB · {timeLabel(p.createdAt, lang)}
        {p.coupon && <span> · 🎟 {p.coupon}</span>}
      </div>
      {p.reason && <div className="mt-1 text-sm text-red-600">{p.reason}</div>}
    </div>
  );
}

/** Pick a package, add a coupon, see how to pay, upload the screenshot, send. */
export function BuySheet({ open, wallet, onClose, onSent }: { open: boolean; wallet: WalletView; onClose: () => void; onSent: () => void }) {
  const t = useT();
  const lang = useLang();
  const errText = useErrorText();
  const [pkg, setPkg] = useState<CreditPackage | null>(null);
  const [code, setCode] = useState('');
  const [quote, setQuote] = useState<{ discount: number; total: number } | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<{ key: string; preview: string } | null>(null);
  const [busy, setBusy] = useState<null | 'upload' | 'coupon' | 'send'>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const key = useRef(actionId());
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setPkg(wallet.packages.length === 1 ? wallet.packages[0] : null);
    setCode('');
    setQuote(null);
    setCodeError(null);
    setEvidence(null);
    setError(null);
    setSent(false);
    key.current = actionId();
  }, [open]);

  useEffect(() => setQuote(null), [pkg?.id]);

  const applyCoupon = async () => {
    if (!pkg || !code.trim()) return;
    setBusy('coupon');
    setCodeError(null);
    try {
      const q = await api.checkCoupon(code.trim(), pkg.id);
      setQuote(q);
      haptic('success');
    } catch (e) {
      setQuote(null);
      setCodeError(errText(e));
      haptic('error');
    } finally {
      setBusy(null);
    }
  };

  const pickFile = async (f: File | undefined) => {
    if (!f) return;
    setBusy('upload');
    setError(null);
    try {
      const r = await api.upload('payment_evidence', f, f.name);
      setEvidence({ key: r.key, preview: URL.createObjectURL(f) });
      haptic('success');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!pkg) return;
    setBusy('send');
    setError(null);
    try {
      await api.pay({ packageId: pkg.id, couponCode: quote ? code.trim() : undefined, evidenceKey: evidence?.key, idempotencyKey: key.current });
      haptic('success');
      setSent(true);
      onSent();
    } catch (e) {
      haptic('error');
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const total = quote ? quote.total : pkg?.price ?? 0;

  return (
    <BottomSheet open={open} onClose={onClose}>
      {sent ? (
        <div className="flex flex-col items-center py-4 text-center">
          <CheckCircle2 size={64} className="mb-3 text-green-500" />
          <h3 className="mb-2 text-xl font-bold">{t('pay.sent_title')}</h3>
          <p className="mb-5 text-tg-hint">{t('pay.sent_body')}</p>
          <PrimaryButton onClick={onClose}>{t('common.close')}</PrimaryButton>
        </div>
      ) : (
        <div>
          <h3 className="mb-3 text-xl font-bold">{t('pay.choose_package')}</h3>
          <div className="space-y-2">
            {wallet.packages.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  haptic('select');
                  setPkg(p);
                }}
                className={`flex w-full items-center justify-between rounded-2xl border-2 px-4 py-3.5 text-left ${
                  pkg?.id === p.id ? 'border-tg-button bg-tg-secondary' : 'border-transparent bg-tg-secondary'
                }`}
              >
                <span>
                  <span className="block font-semibold">{lang === 'am' ? p.nameAm : p.nameEn}</span>
                  <span className="text-sm text-tg-hint">{t('pay.package_line', { credits: p.credits })}</span>
                </span>
                <span className="text-lg font-bold">{etb(p.price)} ETB</span>
              </button>
            ))}
          </div>

          {pkg && wallet.couponsEnabled && (
            <div className="mt-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Ticket size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-tg-hint" />
                  <input
                    className={inputClass + ' pl-10 uppercase'}
                    placeholder={t('pay.coupon')}
                    value={code}
                    maxLength={32}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setQuote(null);
                      setCodeError(null);
                    }}
                  />
                </div>
                <button
                  onClick={applyCoupon}
                  disabled={!code.trim() || busy === 'coupon'}
                  className="shrink-0 rounded-xl bg-tg-secondary px-4 font-semibold text-tg-link disabled:opacity-50"
                >
                  {t('pay.coupon_apply')}
                </button>
              </div>
              {quote && <p className="mt-1.5 text-sm font-medium text-green-700">{t('pay.coupon_ok', { amount: etb(quote.discount) })}</p>}
              {codeError && <p className="mt-1.5 text-sm text-red-600">{codeError}</p>}
            </div>
          )}

          {pkg && (
            <>
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-tg-secondary px-4 py-3">
                <span className="text-tg-hint">{t('pay.total')}</span>
                <span className="text-2xl font-bold">{etb(total)} ETB</span>
              </div>
              <div className="mt-4 rounded-2xl border border-black/10 p-4">
                <h4 className="mb-1 font-semibold">{t('pay.how_title')}</h4>
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{wallet.instructions}</p>
              </div>

              <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
              <button
                onClick={() => file.current?.click()}
                className="mt-4 flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-black/15 p-3 text-left active:bg-tg-secondary"
              >
                {evidence ? (
                  <img src={evidence.preview} alt="" className="h-16 w-16 rounded-xl object-cover" />
                ) : (
                  <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-tg-secondary text-tg-hint">
                    {busy === 'upload' ? <Preloader className="h-6 w-6" /> : <ImagePlus size={26} />}
                  </span>
                )}
                <span className="font-medium">{evidence ? t('pay.uploaded') : busy === 'upload' ? t('reg.uploading') : t('pay.upload')}</span>
              </button>
            </>
          )}

          {error && <p className="mt-3 rounded-xl bg-red-500/10 p-3 text-red-600">{error}</p>}
          <PrimaryButton className="mt-4" disabled={!pkg || (!evidence && total > 0) || busy !== null} onClick={send}>
            {busy === 'send' ? <Preloader className="h-5 w-5" /> : t('pay.submit')}
          </PrimaryButton>
        </div>
      )}
    </BottomSheet>
  );
}
